import { Component, computed, input } from '@angular/core';

import { InfoscreenWeather } from '../infoscreen-module.types';
import { degrees, hourLabel } from './weather-card.component';
import { WEATHER_LABELS, WeatherIconComponent } from './weather-icon.component';

const COMPASS = ['N', 'NØ', 'Ø', 'SØ', 'S', 'SV', 'V', 'NV'];

/**
 * The forecast as a page the screen changes to now and then (the weather "as a page"): the
 * weather now, large, and the next twelve hours one by one with rain. Laid over the times, in the
 * theme's colours.
 */
@Component({
  selector: 'app-weather-page',
  imports: [WeatherIconComponent],
  template: `
    <div class="title">Vejret</div>
    <div class="now">
      <app-weather-icon class="big" [condition]="weather().condition" [night]="weather().night" />
      <div>
        <div class="temp">{{ degrees(weather().temperature) }}</div>
        <div class="label">{{ label() }}</div>
        <div class="wind">Vind {{ windSpeed() }} m/s fra {{ windFrom() }}</div>
      </div>
    </div>
    <div class="hours">
      @for (hour of next(); track hour.time) {
        <div class="hour">
          <div class="at">{{ hourLabel(hour.time) }}</div>
          <app-weather-icon [condition]="hour.condition" [night]="hour.night" />
          <div class="t">{{ degrees(hour.temperature) }}</div>
          <div class="rain">{{ hour.precipitation > 0 ? rainLabel(hour.precipitation) : '' }}</div>
        </div>
      }
    </div>
    <div class="source">Vejrdata: {{ weather().source }}</div>
  `,
  styles: `
    :host {
      --u: calc(var(--screen-h, 100vh) / 100);
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: calc(var(--u) * 4);
      box-sizing: border-box;
      padding: calc(var(--u) * 3) var(--gutter-size, 4%);
      color: rgb(var(--ink-rgb, 255, 255, 255));
    }
    .title {
      font-size: calc(var(--u) * 4);
      font-weight: 700;
      opacity: 0.85;
    }
    .now {
      display: flex;
      align-items: center;
      gap: calc(var(--u) * 3);
    }
    .big {
      font-size: calc(var(--u) * 20);
    }
    .temp {
      font-size: calc(var(--u) * 15);
      font-weight: 800;
      line-height: 1;
    }
    .label {
      font-size: calc(var(--u) * 4.5);
      opacity: 0.9;
    }
    .wind {
      font-size: calc(var(--u) * 3);
      opacity: 0.85;
      margin-top: calc(var(--u) * 0.6);
    }
    .hours {
      display: grid;
      grid-template-columns: repeat(12, minmax(0, 1fr));
      width: 100%;
      gap: calc(var(--u) * 1);
      text-align: center;
    }
    .hour {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: calc(var(--u) * 0.6);
      font-size: calc(var(--u) * 2.6);
      app-weather-icon {
        font-size: calc(var(--u) * 6);
      }
    }
    .at {
      opacity: 0.8;
    }
    .t {
      font-weight: 700;
    }
    .rain {
      font-size: calc(var(--u) * 1.9);
      color: #9fd8ff;
      min-height: 1.2em;
    }
    .source {
      font-size: calc(var(--u) * 1.6);
      opacity: 0.7;
    }
    /* A portrait (narrow) screen: six hours, with room to read them. */
    @container infoscreen-container (max-width: 1400px) {
      .hours {
        grid-template-columns: repeat(6, minmax(0, 1fr));
      }
      .hour:nth-child(n + 7) {
        display: none;
      }
    }
  `,
})
export class WeatherPageComponent {
  readonly weather = input.required<InfoscreenWeather>();

  readonly degrees = degrees;
  readonly hourLabel = hourLabel;
  readonly label = computed(() => WEATHER_LABELS[this.weather().condition]);
  readonly next = computed(() => this.weather().hours.slice(0, 12));
  readonly windSpeed = computed(() => Math.round(this.weather().windSpeed));
  /** Where the wind comes from, as on a Danish weather map: N, NØ, Ø, SØ, S, SV, V, NV. */
  readonly windFrom = computed(() => COMPASS[Math.round((((this.weather().windFromDirection % 360) + 360) % 360) / 45) % 8]);

  rainLabel(mm: number): string {
    return `${mm.toLocaleString('da-DK', { maximumFractionDigits: 1 })} mm`;
  }
}
